import { DeviceMode, isSlotElement, type NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import React, { useCallback, useMemo } from 'react'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import { ELEMENTS_CONFIG } from 'components/Editor/EditorElements/elements.config'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import KeyBindTag from 'components/Editor/components/KeyBindTag/KeyBindTag'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { setHighlight, setProjectNavigation } from 'services/Store/Project/actions'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getIsActiveShiftKey,
  getIsEditingGrid,
  getNodeStyle,
  getParents,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import RootPicker from '../../RootPicker'
import { onEditGrid, onLock } from '../../commands'
import { onDrag } from '../../commands/onDrag'
import { NODES_ICON_MAP } from '../../displayIconsMap'
import { getNodeLabel } from '../../getNodeLabel'
import { isGrid, isRoot as checkIsRoot } from '../../guards'
import { useContentPlus } from '../../useContentPlus'
import NodeV2CommandsMenu from '../NodeV2CommandsMenu'
import * as s from './NodeV2Label.module.scss'

interface INodeV2SingleLabelProps {
  mode: IBlockMode
  block: Block
  parent: NodeV2 | null
  node: NodeV2
}

const NodeV2SingleLabel: React.FC<INodeV2SingleLabelProps> = (props) => {
  const { mode, block, parent, node } = props
  const isDesktop = mode.deviceMode === DeviceMode.desktop
  const isPro = mode.editorMode === AppMode.pro
  const dispatch = useProjectDispatch()
  const isContentPlus = useContentPlus()
  const parentStyles = useProjectContext(getNodeStyle, block.uuid, parent?.id)
  const styles = useProjectContext(getNodeStyle, block.uuid, node.id)
  const isEditingGrid = useProjectContext(getIsEditingGrid, node.id)
  const label = getNodeLabel(block, node, styles)
  const isRoot = checkIsRoot(block, node)
  const isShift = useProjectContext(getIsActiveShiftKey)
  const parents = useProjectContext(getParents, block.uuid, node.id)
  const type = styles?.display || parentStyles?.display || 'element'
  const isSlot = useMemo(() => {
    const parent = getParentNode(node.id, block)
    const element = parent?.elementId ? block.elements[parent.elementId] : null
    return Boolean(isSlotElement(element) && element.value.item?.value)
  }, [block, node.id])

  const parentItems = useMemo(() => {
    return parents.map((node) => {
      const style = mergeNodeStyle(node.style, mode.deviceMode)
      const label = getNodeLabel(block, node, style)

      const icon = node.elementId ? (
        <img className={s.icon} src={ELEMENTS_CONFIG[block.elements[node.elementId].type].icon} />
      ) : (
        <Icon className={s.icon} name={NODES_ICON_MAP[style.display || 'flex']} size={12} />
      )
      return { id: node.id, node, style, label, icon }
    })
  }, [block, mode.deviceMode, parents])

  const canDrag =
    !isSlot &&
    (isPro || isContentPlus) &&
    (mode.deviceMode === DeviceMode.desktop ||
      styles?.display === 'absolute' ||
      styles?.display === 'grid')

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      onDrag(event, {
        mode: mode.editorMode,
        blockId: block.uuid,
        source: getParentNode(node.id, block),
        targets: [node],
        isContentPlus,
      })
    },
    [mode.editorMode, block, node, isContentPlus],
  )

  const handleParentMouseDown = useCallback(
    (event: React.MouseEvent, node: NodeV2) => {
      event.preventDefault()
      dispatch(setProjectNavigation({ nodeIds: [node.id] }))
    },
    [dispatch],
  )

  const handleParentMouseEnter = (e: React.MouseEvent, node: NodeV2) => {
    e.stopPropagation()
    dispatch(setHighlight(node.id))
  }

  const handleParentMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(setHighlight(null))
  }

  if (!isEditingGrid) {
    return (
      <AbsolutePortal
        chain='off'
        name='title'
        offset={`${-ABSOLUTE_PORTAL_GAP}px 0`}
        placement='topLeft'
        recalculatingKey={block}
        translateY='-100%'
      >
        <div className={s.multiLabel}>
          <div className={s.root}>
            {isShift &&
              isPro &&
              parentItems.map((item) => (
                <div
                  className={cn(s.item, s.active, s[item.style.display || 'element'])}
                  key={item.id}
                  onMouseDown={(e) => handleParentMouseDown(e, item.node)}
                  onMouseOut={handleParentMouseLeave}
                  onMouseOver={(e) => handleParentMouseEnter(e, item.node)}
                >
                  {item.icon}
                  <Typography
                    color='inherit'
                    indents={{ padding: '0 2px' }}
                    rows={1}
                    size={KitSize.S}
                  >
                    {item.label}
                  </Typography>
                </div>
              ))}
            <div className={cn(s.item, s[type])}>
              {!isRoot && canDrag && (
                <Icon
                  className={s.drag}
                  name='drag'
                  onMouseDown={handleMouseDown}
                  size={KitSize.XS}
                />
              )}
              <Typography color='inherit' indents={{ padding: '0 2px' }} rows={1} size={KitSize.S}>
                {label}
              </Typography>
              {isPro && isGrid(styles) && (
                <Tooltip
                  mouseEnterDelay={0.5}
                  overlay={
                    <div className={s.label}>
                      {t('editorV2.grid.edit')}
                      <KeyBindTag bind='ctrl+E' dark />
                    </div>
                  }
                >
                  <IconButton
                    icon='gear'
                    name='gear'
                    onClick={onEditGrid}
                    size={KitSize.XS}
                    styleType='ghost'
                    inheritColor
                  />
                </Tooltip>
              )}
              {node.isDone && (isPro || isContentPlus) && (
                <Tooltip
                  mouseEnterDelay={0.5}
                  overlay={isPro && <KeyBindTag bind='shift+L' dark />}
                  placement='top'
                >
                  <Icon
                    color='var(--color-disabled)'
                    name='lock'
                    onClick={onLock}
                    size={KitSize.XS}
                  />
                </Tooltip>
              )}
              {!isRoot && (isPro || isContentPlus) && (
                <NodeV2CommandsMenu
                  block={block}
                  color={styles?.display || parentStyles?.display}
                  mode={mode}
                  node={parent}
                  selected={node}
                />
              )}
            </div>
          </div>
          {isRoot && isDesktop && isPro /* (isPro || isContentPlus) */ && (
            <RootPicker block={block} node={node} type={type} />
          )}
        </div>
      </AbsolutePortal>
    )
  }
  return null
}

export default React.memo(NodeV2SingleLabel)
